import React from 'react';
import { ArrowUpRight, FileSpreadsheet, Loader2, Wand2, X } from 'lucide-react';
import type { AssistantTaskListItem } from './assistantTaskLaneState';

type SpreadsheetSessionStatus = {
  title: string;
  workspaceOpen: boolean;
};

type AssistantSpreadsheetComposerStatusProps = {
  activeTask?: AssistantTaskListItem | null;
  session?: SpreadsheetSessionStatus | null;
  onOpenWorkspace?: (() => void) | null;
  onDismiss?: (() => void) | null;
};

const clampProgress = (value: number | null | undefined): number => {
  if (!Number.isFinite(value)) {
    return 18;
  }
  return Math.max(8, Math.min(100, Number(value)));
};

export const AssistantSpreadsheetComposerStatus: React.FC<AssistantSpreadsheetComposerStatusProps> = ({
  activeTask,
  session,
  onOpenWorkspace,
  onDismiss,
}) => {
  if (!activeTask && !session) {
    return null;
  }

  if (activeTask) {
    const progressPercent = clampProgress(activeTask.progressPercent);
    const isModifyTask = activeTask.workflowKey === 'artifact.spreadsheet.modify';
    const isHydratingArtifact = activeTask.state === 'hydrating_artifact';
    return (
      <div
        className="rounded-[22px] border border-primary/20 bg-[linear-gradient(135deg,rgba(243,251,248,0.98),rgba(255,255,255,0.98))] px-4 py-3 shadow-[0_18px_40px_rgba(36,50,53,0.08)] transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity"
        data-testid="ai-composer-spreadsheet-task-status"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/70">
              {isHydratingArtifact ? 'Spreadsheet artifact finalizing' : 'Spreadsheet task running'}
            </p>
            <div className="mt-1 flex items-center gap-2">
              {isHydratingArtifact ? (
                <Loader2 size={14} className="shrink-0 animate-spin text-primary" />
              ) : isModifyTask ? (
                <Wand2 size={14} className="shrink-0 text-primary" />
              ) : (
                <Loader2 size={14} className="shrink-0 animate-spin text-primary" />
              )}
              <p className="truncate text-sm font-semibold text-slate-900">
                {activeTask.title}
              </p>
            </div>
            <p className="mt-1 text-xs text-irongrey/75">
              {activeTask.statusMessage ?? activeTask.phaseLabel ?? 'Updating spreadsheet workspace…'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!session?.workspaceOpen && onOpenWorkspace ? (
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
                onClick={onOpenWorkspace}
                data-testid="ai-composer-spreadsheet-open-workspace"
              >
                Open
                <ArrowUpRight size={12} />
              </button>
            ) : null}
            <div className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary shadow-sm">
              {progressPercent}%
            </div>
            {onDismiss ? (
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-primary/10 bg-white/90 text-irongrey/55 hover:bg-white hover:text-irongrey"
                onClick={onDismiss}
                aria-label="Dismiss spreadsheet status"
                data-testid="ai-composer-spreadsheet-dismiss"
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-primary/10">
          <div
            className="h-2 rounded-full bg-gradient-to-r from-primary to-jungle transition-[width] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-opacity"
            style={{ width: `${progressPercent}%` }}
            data-testid="ai-composer-spreadsheet-task-progress"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-[22px] border border-gray-200 bg-white/95 px-4 py-3 shadow-[0_16px_36px_rgba(36,50,53,0.06)]"
      data-testid="ai-composer-spreadsheet-session"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/70">
            Active spreadsheet context
          </p>
          <div className="mt-1 flex items-center gap-2">
            <FileSpreadsheet size={14} className="shrink-0 text-primary" />
            <p className="truncate text-sm font-semibold text-slate-900">
              {session?.title ?? 'Current spreadsheet'}
            </p>
          </div>
          <p className="mt-1 text-xs text-irongrey/75">
            Ask the assistant to revise rows, columns, formulas, filters, or sheet tabs while your draft stays versioned.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!session?.workspaceOpen && onOpenWorkspace ? (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
              onClick={onOpenWorkspace}
              data-testid="ai-composer-spreadsheet-open-workspace"
            >
              Open
              <ArrowUpRight size={12} />
            </button>
          ) : null}
          {onDismiss ? (
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-irongrey/55 hover:bg-white hover:text-irongrey"
              onClick={onDismiss}
              aria-label="Dismiss spreadsheet status"
              data-testid="ai-composer-spreadsheet-dismiss"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
